import { monto } from "@/componentes/tesoreria/dinero"

/** Un mes del flujo de caja (`GET /informes/flujo-caja`). */
export type MesFlujo = {
  desde: string
  hasta: string
  nombre: string
  ingresos: string
  egresos: string
  neto: string
}

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]

/** «oct 2026» de un `YYYY-MM-DD`, sin pasar por la hora local. */
export function nombreMes(iso: string): string {
  const [anio, mes] = iso.split("-")
  return `${MESES[Number(mes) - 1]} ${anio}`
}

/**
 * Ingresos y egresos de los seis meses CONSECUTIVOS que terminan en el actual (§3.2 del
 * requerimiento: el del 2.0 se saltaba julio y no traía el mes en curso).
 *
 * ⚠ **Cada mes es un botón**: al pulsarlo los reportes enteros pasan a ese mes, con su flujo
 * por banco y sus partidas (el «clic en un mes para ver el detalle»). Barras de HTML y no
 * un SVG: son botones de verdad para el teclado y el lector de pantalla.
 */
export function GraficoFlujo({
  meses,
  seleccionado,
  onElegir,
}: {
  meses: MesFlujo[]
  seleccionado: string | null
  onElegir: (m: MesFlujo) => void
}) {
  const tope = Math.max(1, ...meses.flatMap((m) => [Number(m.ingresos), Number(m.egresos)]))
  const alto = (v: string) => `${Math.max(0, (Number(v) / tope) * 100)}%`
  return (
    <div>
      <div className="flex items-end gap-2" style={{ height: 180 }}>
        {meses.map((m) => {
          const activo = seleccionado === m.desde
          return (
            <button
              key={m.desde}
              type="button"
              onClick={() => onElegir(m)}
              aria-pressed={activo}
              aria-label={`${nombreMes(m.desde)}: ingresos ${monto(m.ingresos)} $, egresos ${monto(m.egresos)} $. Ver el detalle del mes`}
              className={`foco-cristal toque-cristal flex h-full min-w-0 flex-1 flex-col justify-end rounded-md p-1 ${activo ? "activo-cristal" : ""}`}
            >
              <div className="flex h-full items-end justify-center gap-1">
                <span
                  className="w-1/3 rounded-t"
                  style={{ height: alto(m.ingresos), background: "var(--success)" }}
                />
                <span
                  className="w-1/3 rounded-t"
                  style={{ height: alto(m.egresos), background: "var(--destructive)" }}
                />
              </div>
            </button>
          )
        })}
      </div>
      <div className="mt-1 flex gap-2">
        {meses.map((m) => (
          <div key={m.desde} className="min-w-0 flex-1 text-center text-xs">
            <div className="font-medium">{nombreMes(m.desde)}</div>
            <div
              className="hidden tabular-nums sm:block"
              style={{ color: Number(m.neto) < 0 ? "var(--destructive)" : "var(--fg-muted)" }}
            >
              {monto(m.neto)} $
            </div>
          </div>
        ))}
      </div>
      <p
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs"
        style={{ color: "var(--fg-muted)" }}
      >
        <span className="flex items-center gap-1">
          <span
            className="inline-block size-2 rounded-sm"
            style={{ background: "var(--success)" }}
          />
          Ingresos
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block size-2 rounded-sm"
            style={{ background: "var(--destructive)" }}
          />
          Egresos
        </span>
        <span>Debajo, el neto del mes. Pulsa un mes para ver su detalle.</span>
      </p>
    </div>
  )
}
